import React, { useState, useEffect } from "react";
import Header from "../../components/header";
import Loader from "../../components/loader";
import UIButton from "../../components/ui-button";
import Checkbox from "../../components/checkbox";
import Badge from "../../components/badge";
import Card from "../../components/card";
import Panel from "../../components/panel";
import StepContainer from "../../components/step-container";
import CountdownTimer from "../../components/countdown-timer";
import FactRow from "../../components/fact-row";
import { useNavigate, useParams } from "react-router-dom";
import PathHelper from "../../utils/path-helper";
import {
  APP_NAVIGATION,
  LOCALE,
  DOWNGRADE_SKU,
  DATA_EXPIRY_DAYS_AFTER_CANCELLATION,
} from "../../utils/constants";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { readCookie, createCookie } from "../../utils/helpers";
import { sanitizeAlphanumericWithComma } from "../../utils/validation";
import { formatLocalizedDate } from "../../utils/utility-functions";
import FooterV2 from "../../components/footer-v2";
import ArrowLeftIcon from "../../assets/pre-cancel/arrow-left-icon.svg";

const CANCELLED_TIMER = 24 * 3600;
const TIMER_COOKIE = "cancelled_timer_remaining";

const CANCEL_FACTS = [
  { label: "pre_cancel.fact_plan", value: "pre_cancel.fact_plan_value" },
  { label: "pre_cancel.fact_access", value: "pre_cancel.fact_access_value" },
  { label: "pre_cancel.fact_data", value: "pre_cancel.fact_data_value" },
];

const CANCEL_REASONS = [
  {
    label: "cancel_plan_feedback.cancel_reasons.price_expensive",
    value: "Price is too expensive",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.no_features",
    value: "Don't need various features",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.product_not_good",
    value: "Product needs improvement",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.unrecognized_charge",
    value: "I received a charge I don't recognize",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.does_not_meet_needs",
    value: "Does not meet my needs",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.not_free",
    value: "It isn't free",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.limited_content",
    value: "Limited content/updates",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.temporary_need",
    value: "Temporary need",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.competitor_cheaper",
    value: "Competitor is cheaper",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.use_less_often",
    value: "I don't use it often enough",
  },
  {
    label: "cancel_plan_feedback.cancel_reasons.poor_customer_experience",
    value: "Poor customer experience",
  },
  { label: "cancel_plan_feedback.cancel_reasons.other", value: "Other" },
];

function PromoCountdownPanel({ skuSubCategory, onReactivate, onTakeOffer }) {
  const { t } = useTranslation();

  const [secondsLeft, setSecondsLeft] = useState(() => {
    const saved = readCookie(TIMER_COOKIE);
    if (saved !== null) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return CANCELLED_TIMER;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    createCookie(TIMER_COOKIE, String(secondsLeft), 1);
  }, [secondsLeft]);

  const hh = String(Math.floor(secondsLeft / 3600)).padStart(2, "0");
  const mm = String(Math.floor((secondsLeft % 3600) / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  const isReactivate = skuSubCategory === DOWNGRADE_SKU.DOWN0395;

  return (
    <Panel className="!bg-brand-orange-light !border-[#F7CFC5] !rounded-[20px]">
      <div className="flex flex-col gap-2">
        <div className="font-bold text-[22px] leading-[1.2]">
          {t(
            isReactivate
              ? "pre_cancel.holding_reactivate"
              : "pre_cancel.holding_promo",
          )}
        </div>
        <div className="text-[15px] leading-[1.6] text-text-body max-w-[50ch]">
          {t("pre_cancel.changed_mind")}
        </div>
      </div>

      <CountdownTimer
        hours={hh}
        minutes={mm}
        seconds={ss}
        hoursLabel={t("pre_cancel.hours")}
        minutesLabel={t("pre_cancel.minutes")}
        secondsLabel={t("pre_cancel.seconds")}
        caption={t("pre_cancel.left_discount")}
      />
      <UIButton
        variant="block"
        className="cursor-pointer"
        onClick={isReactivate ? onReactivate : onTakeOffer}
      >
        {t(isReactivate ? "pre_cancel.reactivate" : "pre_cancel.take_promo")}
      </UIButton>
    </Panel>
  );
}

function CancelledStep({
  onTakeOffer,
  showFeedback,
  onToggleFeedback,
  onSkip,
  onGoToDashboard,
  pickedReasons,
  onToggleReason,
  otherText,
  onOtherTextChange,
  onSubmitFeedback,
  nextBillingDate,
  planKey,
  unitPrice,
  currencySymbol,
  skuSubCategory,
  onReactivate,
}) {
  const { t } = useTranslation();

  return (
    <StepContainer>
      <div className="flex flex-col gap-3">
        <Badge color="purple">{t("pre_cancel.subscription_cancelled")}</Badge>
        <div className="font-bold text-[clamp(25px,5.2vw,32px)] leading-[1.13] tracking-[-0.01em] max-w-[26ch]">
          {t("pre_cancel.your_sub_cancelled")}
        </div>
        <div className="text-base leading-[1.65] text-text-body max-w-[50ch]">
          {t("pre_cancel.weve_sent")}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:items-start items-center">
        <UIButton variant="secondary" onClick={onToggleFeedback}>
          {t("cancel_plan_feedback.share_your_experience")}
        </UIButton>
        <div className="text-[14px] leading-[1.55] text-text-light max-w-[44ch]">
          {t("cancel_plan_feedback.improving_feedback")}
        </div>
      </div>

      {showFeedback && (
        <Panel>
          <div className="font-bold text-[19px] leading-[1.3]">
            {t("cancel_plan_feedback.what_led_to_cancellation")}
          </div>
          <div className="flex flex-col gap-2">
            {CANCEL_REASONS.map((reason) => {
              const picked = pickedReasons.includes(reason.value);
              return (
                <div
                  key={reason.value}
                  onClick={() => onToggleReason(reason.value)}
                  className={`flex items-center gap-3 w-full text-left cursor-pointer p-[13px_16px] rounded-[14px] text-[15px] text-text-dark transition-all duration-150 border-[1.5px] ${
                    picked
                      ? "bg-brand-purple-light border-brand-purple"
                      : "bg-white border-border-default"
                  }`}
                >
                  <Checkbox checked={picked} />
                  <span className="leading-[1.4]">{t(reason.label)}</span>
                </div>
              );
            })}
          </div>
          {pickedReasons.includes("Other") && (
            <>
              <textarea
                value={otherText}
                onChange={(e) =>
                  onOtherTextChange(
                    sanitizeAlphanumericWithComma(e.target.value),
                  )
                }
                placeholder={t("cancel_plan_feedback.tell_us_more")}
                rows={3}
                maxLength={500}
                className="w-full box-border p-[15px_18px] rounded-[14px] border border-[#DDD5C8] bg-[#FBF8F3] text-[15px] leading-[1.5] text-text-dark outline-none resize-y focus:border-brand-purple focus:bg-white"
              />
              <div className="text-xs text-text-light text-right">
                {otherText.length}/500
              </div>
            </>
          )}
          <div className="flex items-center gap-[18px] flex-wrap justify-center sm:justify-start">
            <UIButton variant="secondary" onClick={onSubmitFeedback}>
              {t("cancel_plan_feedback.submit_feedback")}
            </UIButton>
            <UIButton variant="link" onClick={onSkip}>
              {t("cancel_plan_feedback.skip")}
            </UIButton>
          </div>
        </Panel>
      )}

      <Panel className="!p-[22px_24px] !gap-3">
        {CANCEL_FACTS.map((f) => {
          if (f.label === "pre_cancel.fact_plan" && planKey) {
            return (
              <FactRow
                key={f.label}
                label={t(f.label)}
                value={`${planKey} — ${currencySymbol}${unitPrice}/mo`}
              />
            );
          }
          if (f.label === "pre_cancel.fact_access" && nextBillingDate) {
            const formattedDate = formatLocalizedDate(nextBillingDate, t);
            if (formattedDate) {
              return (
                <FactRow
                  key={f.label}
                  label={t(f.label)}
                  value={formattedDate}
                />
              );
            }
          }
          if (f.label === "pre_cancel.fact_data") {
            return (
              <FactRow
                key={f.label}
                label={t(f.label)}
                value={`${DATA_EXPIRY_DAYS_AFTER_CANCELLATION} ${t("cancel_plan_feedback.days")}`}
              />
            );
          }
          return (
            <FactRow key={f.label} label={t(f.label)} value={t(f.value)} />
          );
        })}
      </Panel>

      <PromoCountdownPanel
        skuSubCategory={skuSubCategory}
        onReactivate={onReactivate}
        onTakeOffer={onTakeOffer}
      />

      <div className="text-sm text-text-light leading-[1.6]">
        {t("pre_cancel.after_24_hours")}{" "}
        <a href="#contact" className="text-text-light">
          {t("pre_cancel.contact_support")}
        </a>
        .
      </div>
      <hr />
      <div className="flex justify-center">
        <UIButton
          variant="outline"
          onClick={onGoToDashboard}
          className="flex items-center gap-2 !border-[1.5px] !border-solid border-border-input text-text-dark transition-colors duration-150 hover:border-brand-purple hover:text-brand-purple hover:bg-white"
        >
          <img src={ArrowLeftIcon} alt={"arrow"} className="w-5 h-5" />
          {t("cancel_plan_feedback.back_to_dashboard")}
        </UIButton>
      </div>
    </StepContainer>
  );
}

function CancelPlanFeedBack({
  funnelStep,
  submitCancellationFeedbackConnect,
  AccountReActivateConnect,
  getOrderDataByUserIdConnect,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const preCancelPath = PathHelper(APP_NAVIGATION.PRE_CANCEL, currentLocale);
  const [isLoading, setIsLoading] = useState(false);

  const [showFeedback, setShowFeedback] = useState(false);
  const [pickedReasons, setPickedReasons] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const { userGUID, isAuth, nextBillingDate, selectedPlan } = funnelStep || {};

  const planKey = selectedPlan?.planKey ?? "";
  const unitPrice = selectedPlan?.unitPrice ?? "";
  const currencySymbol = selectedPlan?.currencySymbol ?? "";
  const skuSubCategory = selectedPlan?.skuSubCategory ?? "";
  const loginPath = PathHelper(APP_NAVIGATION.SINI, currentLocale);

  useEffect(() => {
    if (!isAuth) {
      navigate(loginPath.next);
    }
  }, [isAuth, navigate, loginPath.next]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (userGUID) {
      getOrderDataByUserIdConnect(userGUID)
        .then((res) => {
          if (res && Object.keys(res).length > 0) {
            setOrderData(res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch order data:", err);
        });
    }
  }, [userGUID, getOrderDataByUserIdConnect]);

  const dashboardPath = PathHelper(APP_NAVIGATION.DASHBOARD_B, currentLocale);

  const handleTakeOffer = () => {
    navigate(`${preCancelPath.next}?step=2&fromCancelled=true`);
  };

  const handleReactivate = async () => {
    const data = {
      transactionId: orderData?.transactionDetailId,
      isCancelled: false,
    };

    setIsReactivating(true);

    try {
      const response = await AccountReActivateConnect(data);

      if (response) {
        toast.success(t("settings.account_reactivate_msg"));
        navigate(dashboardPath.next);
      } else {
        setIsReactivating(false);
        toast.error(t("settings.reactivate_fail_msg"));
      }
    } catch (err) {
      console.error("Reactivation failed:", err);
      setIsReactivating(false);
      toast.error(t("settings.reactivate_fail_msg"));
    }
  };

  const handleToggleFeedback = () => {
    setShowFeedback((prev) => !prev);
  };

  const handleSkip = () => {
    navigate(dashboardPath.next);
  };

  const handleGoToDashboard = () => {
    navigate(dashboardPath.next);
  };

  const handleToggleReason = (label) => {
    setPickedReasons((prev) =>
      prev.includes(label) ? prev.filter((r) => r !== label) : [...prev, label],
    );
  };

  const handleSubmitFeedback = async () => {
    setIsLoading(true);
    try {
      const hasOther = pickedReasons.includes("Other");
      const selectedReasons = pickedReasons.filter((r) => r !== "Other");
      await submitCancellationFeedbackConnect({
        userId: userGUID || null,
        reason: [
          ...selectedReasons,
          ...(otherText.trim() ? [otherText.trim()] : []),
        ].join(", "),
        reasonType: hasOther ? "Other" : "Default",
        locale: currentLocale,
      });

      toast.success(t("cancel_plan_feedback.feedback_success"));
      setShowFeedback(false);
      setPickedReasons([]);
      setOtherText("");
      navigate(dashboardPath.next);
    } catch (err) {
      toast.error(t("cancel_plan_feedback.feedback_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading || isReactivating} />
      <Header />
      <div className="flex justify-center p-4 bg-surface-card min-h-[calc(100vh-160px)]">
        <Card className="max-w-[800px]">
          <CancelledStep
            onTakeOffer={handleTakeOffer}
            showFeedback={showFeedback}
            onToggleFeedback={handleToggleFeedback}
            onSkip={handleSkip}
            onGoToDashboard={handleGoToDashboard}
            pickedReasons={pickedReasons}
            onToggleReason={handleToggleReason}
            otherText={otherText}
            onOtherTextChange={setOtherText}
            onSubmitFeedback={handleSubmitFeedback}
            nextBillingDate={nextBillingDate}
            planKey={planKey}
            unitPrice={unitPrice}
            currencySymbol={currencySymbol}
            skuSubCategory={skuSubCategory}
            onReactivate={handleReactivate}
          />
        </Card>
      </div>
      <FooterV2 />
    </>
  );
}

export default CancelPlanFeedBack;
